import { CheckCircle } from "@mui/icons-material";
import {
  Box,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
// @ts-ignore
import CustomerXlsx from "assets/bulkCustomerTemplate.xlsx";
import CsvFileTypeImg from "assets/images/CsvFileType.webp";
import XlsxFileTypeImg from "assets/images/XlsxFileType.webp";
// @ts-ignore
import UserXlsx from "assets/users.xlsx";
import {
  Accordian,
  CloseIcon,
  DownloadIcon,
  FileIcon,
  UploadIcon,
} from "components";
import saveAs from "file-saver";
import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import { AttachmentData } from "shared/features/attachment/attachmentType";
import { v4 as uuidv4 } from "uuid";

function BulkUpload({
  fileUploadState,
  setFileUploadState,
  isLoading,
  type,
}: {
  fileUploadState: any;
  setFileUploadState: any;
  isLoading: boolean;
  type: "users" | "customers";
}) {
  const theme = useTheme();

  const title = `Bulk upload ${type} with .XLSX or .CSV files`;

  const onDrop = useCallback((acceptedFiles: any) => {
    const key = uuidv4();
    const acceptedFile = acceptedFiles?.[0] || null;
    if (acceptedFile) {
      const data: AttachmentData = {
        key: key,
        name: acceptedFile?.name,
        size: acceptedFile?.size,
        type: acceptedFile?.type,
        status: "Uploading",
        progress: 0,
        file: acceptedFile,
        responseData: null,
      };
      setFileUploadState((prev: any) => ({
        ...prev,
        fileToBeUploaded: data?.file && data?.file,
      }));
    }
  }, []);

  const sizeValidator = (file: any) => {
    if (file.size > 10485760) {
      toast.error("File size larger than 10MB");
      return {
        code: "size-too-large",
        message: "Size is larger than expected",
      };
    }
    return null;
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected: (fileRejections) => {
      toast.error(
        "Invalid file type. Please upload a valid .xlsx or .csv file",
      );
    },
    // accept: {
    //   "image/*": [".png", ".jpe", ".jpeg", ".jpg", ".webp"],
    // },
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "text/csv": [".csv"],
    },
    validator: sizeValidator,
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        flex: 1,
      }}
    >
      <Stack width="100%" px="24px" gap="24px">
        <Typography variant="h5">{title}</Typography>

        <Stack gap="16px">
          <Stack
            width="100%"
            direction={"row"}
            alignItems={"center"}
            justifyContent={"center"}
            gap="4px"
            sx={{
              cursor: "pointer",
            }}
            onClick={() => {
              type === "users"
                ? saveAs(UserXlsx, "User_import_template.xlsx")
                : saveAs(CustomerXlsx, "Customer_import_template.xlsx");
            }}
          >
            <Typography
              variant="button-label"
              color={theme.palette.purple.main}
              lineHeight={"16px"}
            >
              Download template file
            </Typography>
            <DownloadIcon
              sx={{
                color: theme.palette.purple.main,
              }}
              style={{
                width: "24px",
                height: "24px",
              }}
            />
          </Stack>
          {fileUploadState?.fileToBeUploaded ? (
            <Stack
              sx={{
                border: "1px solid rgba(227, 227, 227, 1)",
                borderRadius: "4px",
              }}
            >
              <Stack
                direction={"row"}
                sx={{
                  justifyContent: "space-between",
                  paddingLeft: "8px",
                }}
              >
                <Stack
                  direction={"row"}
                  sx={{
                    gap: "16px",
                    alignItems: "center",
                  }}
                >
                  <FileIcon type={"text/csv"} />
                  <Typography
                    variant="body-small"
                    color={theme.palette.lightBg.high}
                  >
                    {/* @ts-ignore */}
                    {fileUploadState?.fileToBeUploaded?.name || "No file"}
                  </Typography>
                </Stack>
                <IconButton
                  onClick={() => {
                    setFileUploadState((prev: any) => ({
                      ...prev,
                      isSuccess: "pending",
                      errorMessages: [],
                      fileToBeUploaded: null,
                    }));
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
              {isLoading && (
                <LinearProgress
                  color="success"
                  sx={{
                    height: 2,
                    "& .MuiLinearProgress-bar": {
                      animationDuration: "3s",
                    },
                  }}
                />
              )}
            </Stack>
          ) : (
            <Stack
              {...getRootProps()}
              bgcolor={theme.palette.secondary["purple-tint-3"]}
              border={`${isDragActive ? "2px" : "1px"} dashed ${
                theme.palette.primary.main
              }`}
              borderRadius={"8px"}
              alignItems={"center"}
              justifyContent={"center"}
              gap="8px"
              direction={"row"}
              py="10px"
              sx={{ cursor: "pointer" }}
            >
              <input {...getInputProps()} />
              <UploadIcon
                fontSize="large"
                sx={{
                  color: theme.palette.purple.main,
                }}
              />
              <Stack alignItems={"center"}>
                <Typography variant="body-medium">
                  Add your file here
                </Typography>
                <Typography variant="body-xsmall" color="lightBg.low">
                  Supports .csv, .xlsx
                </Typography>
              </Stack>
            </Stack>
          )}
        </Stack>
        <Instructions type={type} />
        {fileUploadState?.isSuccess === "failed" ? (
          <Accordian
            defaultExpanded
            title={"Errors"}
            accordianContainerStyles={{
              padding: 0,
              borderBottom: `1px solid ${theme.palette.neutral["010"]}`,
              backgroundColor: "unset",
              "&.MuiPaper-root.MuiPaper-elevation.MuiPaper-elevation0.MuiAccordion-root::before":
                {
                  backgroundColor: "unset",
                },
              "&.Mui-expanded": {
                borderBottom: "1px solid transparent",
              },
            }}
            accordianSummaryStyles={{
              height: "55px",
              minHeight: "55px",
              "&.Mui-expanded": {
                borderBottom: `1px solid ${theme.palette.neutral["010"]}`,
              },
            }}
          >
            <Stack direction={"column"} width={"100%"} gap={"16px"}>
              {fileUploadState?.errorMessages.map((error: any) => (
                <Stack
                  direction="row"
                  alignItems={"center"}
                  key={typeof error === "object" ? error?.message : error}
                  sx={{ alignItems: "top", gap: "16px" }}
                >
                  <CloseIcon color="error" />
                  <Typography
                    variant="body-medium"
                    color={theme.palette.lightBg.low}
                  >
                    {typeof error === "object"
                      ? `Row ${error?.errors?.row_number} | ${type === "customers" ? error?.errors?.customer_name || "Invalid customer name" : error?.errors?.customer_user_name || "Invalid user name"} : ${error?.errors?.message}`
                      : error}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Accordian>
        ) : fileUploadState?.isSuccess === "success" ? (
          <Stack
            direction="row"
            alignItems={"center"}
            gap="8px"
            p="8px 16px"
            bgcolor={theme.palette.success.tint}
          >
            <CheckCircle
              color="success"
              sx={{ width: "16px", height: "16px" }}
            />
            <Typography variant="body-small">
              Successfully added {type === "users" ? "users" : "customers"}.
            </Typography>
          </Stack>
        ) : (
          <></>
        )}
        <Divider sx={{ marginTop: "0px" }} />
        <Stack direction="row" alignItems="center" gap="16px">
          <Typography variant="body-medium" color={theme.palette.lightBg.low}>
            Supported file types :
          </Typography>
          <Stack direction="row" gap="12px">
            <img
              src={XlsxFileTypeImg}
              alt="XLSX file type"
              height="44px"
              width="40px"
            />
            <img
              src={CsvFileTypeImg}
              alt="CSV file type"
              height="44px"
              width="40px"
            />
          </Stack>
        </Stack>
        <Stack
          direction={"row"}
          sx={{
            display: "flex",
            alignItems: "center",
          }}
          gap={"8px"}
        >
          <Typography
            variant="body-medium"
            color={"#767B85"}
            className="imageCardTitle"
          >
            Need help?
          </Typography>
          <Typography
            className="imageCardTitle"
            variant="body-medium"
            sx={{
              color: "#5563F5",
              textDecoration: "underline",
              cursor: "pointer",
            }}
            onClick={() => {
              window.open(
                type === "users"
                  ? "https://support.deskday.com/en/articles/8236127-adding-bulk-number-of-users-inside-a-customer"
                  : "https://support.deskday.com/en/articles/8236111-adding-bulk-number-of-customers",
                "_blank",
              );
            }}
          >
            Learn how to add bulk {type === "users" ? "users" : "customers"}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

const instructionsContent = {
  users: {
    headers: ["User name", "Email"],
    optionalHeaders: ["User type", "Site"],
    limit: "500 users",
  },
  customers: {
    headers: ["Customer name"],
    optionalHeaders: [
      "Domain name",
      "Address 1",
      "Address 2",
      "Zip/Postal\u00A0code",
    ],
    limit: "100 customers",
  },
};

const Instructions = ({ type }: { type: "users" | "customers" }) => {
  const { headers, optionalHeaders, limit } = instructionsContent[type];
  return (
    <Stack gap="16px" mt="16px">
      <Stack direction="row" gap="4px">
        <Typography variant="body-medium">1.</Typography>
        <Typography variant="body-medium">
          Use clear column headers (
          {headers.map((header, index) => (
            <Typography component="span" variant="subtitle1" key={header}>
              {index > 0 && ", "}
              {header}
              <Typography
                component="span"
                variant="subtitle1"
                color="error.main"
              >
                *
              </Typography>
            </Typography>
          ))}
          {optionalHeaders.length > 0 && (
            <Typography component="span" variant="subtitle1">
              , {optionalHeaders.join(", ")}
            </Typography>
          )}
          ).
        </Typography>
      </Stack>
      <Stack direction="row" gap="4px">
        <Typography variant="body-medium">2.</Typography>
        <Typography variant="body-medium">
          Maximum file size:{" "}
          <Typography component="span" variant="subtitle1">
            10 MB.
          </Typography>
        </Typography>
      </Stack>
      <Stack direction="row" gap="4px">
        <Typography variant="body-medium">3.</Typography>
        <Typography variant="body-medium">
          You can upload up to{" "}
          <Typography component="span" variant="subtitle1">
            {limit} per file.
          </Typography>
        </Typography>
      </Stack>
    </Stack>
  );
};

export default BulkUpload;
